import { getColumnKey } from '@blaze-shared/schema-domain';
import { isNullish } from '@blaze-shared/shared-util';
import { type DataAssignment } from '@blaze-shared/workflow-domain';
import { getInput1DisplayInfo } from '@blaze-shared/workflow-domain';

import {
  type ActionConfiguratorFieldsConfig,
  type ActionConfiguratorFormField,
} from './action-configurator.model';

export function getFieldsConfig(
  fields: ActionConfiguratorFormField[] | undefined,
  includeEmptyStrings?: boolean,
): ActionConfiguratorFieldsConfig {
  // Omit fields without any value provided which are not shown on the form
  const fieldsToPass = (fields || []).filter((field) => {
    return (
      field.showOnForm ||
      (!isNullish(field.value) && typeof field.value !== 'string') || // show when field value isn't a string
      (!includeEmptyStrings && !!field.value) ||
      (includeEmptyStrings && !isNullish(field.value))
    );
  });
  const passThroughAssignments: DataAssignment[] = fieldsToPass.map((field) => ({
    name: getColumnKey(field),
    displayInfos: [getInput1DisplayInfo(getColumnKey(field))],
    value: `$$input1["${getColumnKey(field)}"]`,
    isFormulaValue: true,
    isOptional: field.isOptional ?? true, // for backwards compatibility defaults to true when not set
  }));
  const defaultAssignments: DataAssignment[] = fieldsToPass.map((field) => ({
    name: getColumnKey(field),
    value: getFieldValue(field),
    displayInfos: field.displayInfos,
    displayInfosObject: field.displayInfosObject ?? {},
    isFormulaValue: field.isFormulaValue ?? false,
    isOptional: field.isOptional ?? true, // for backwards compatibility defaults to true when not set
  }));

  return { fieldsToPass, passThroughAssignments, defaultAssignments };
}

export function parseAssignmentsForApiCall(assignments: DataAssignment[]): DataAssignment[] {
  return assignments.map((assignment) => {
    try {
      const assignmentValue = JSON.parse(assignment.value);
      return { ...assignment, value: assignmentValue };
    } catch {
      return assignment;
    }
  });
}

function getFieldValue(field: ActionConfiguratorFormField): string {
  const value = field.value;

  // This case happens when using array params and saving data in displayInfosObject.
  if (Array.isArray(value) && field.isObjectValue) {
    return JSON.stringify(value);
  }

  if (isNullish(value)) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'boolean' || typeof value === 'number') {
    return value;
  }
  if (Array.isArray(value)) {
    return (value as any[]).join(',');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  throw new Error('Unable to parse assignment value');
}
